'use client';

import { Elements } from '@/components/elements';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from 'react-router';
import { useCallback, useMemo, useState } from 'react';

export type UseCasesWhyHowPill = {
    id: string;
    label: string;
};

export type UseCasesWhyHowInnerCard = {
    iconSrc: string;
    iconAlt?: string;
    title: string;
    description: string;
};

export type UseCasesWhyHowBlock = {
    title: string;
    description: string;
    /** Two or three cards per row on desktop. */
    cards: UseCasesWhyHowInnerCard[];
    ctaLabel?: string;
    ctaHref?: string;
    /** Optional classes on the outer panel (title + cards + CTA) */
    containerClassName?: string;
    /** DOM id for this block; defaults to `usecases-whyhow-{index}`. Must match manual `pillItems[].id` when used. */
    anchorId?: string;
    /** Shorter label for the nav pill when using `pillNavMode="fromBlocks"`. */
    pillNavLabel?: string;
};

export type UseCasesWhyHowPillNavMode = 'off' | 'fromBlocks';

export type UseCasesWhyHowProps = {
    /** Centered heading above the pill row */
    sectionTitle: string;
    sectionTitleClassName?: string;
    /**
     * Pill labels. Omit or pass [] and set `pillNavMode="fromBlocks"` to build pills from `blocks`
     * (uses `pillNavLabel` or `title` per block).
     */
    pillItems?: UseCasesWhyHowPill[];
    /** When `fromBlocks`, show a pill per block and scroll to the matching card panel on click. */
    pillNavMode?: UseCasesWhyHowPillNavMode;
    /** Uncontrolled default (first item if omitted) */
    defaultActivePillId?: string;
    /** Controlled active id */
    activePillId?: string;
    onActivePillChange?: (id: string) => void;
    /** Repeatable block groups (title, body, 2–3 feature cards, CTA) */
    blocks: UseCasesWhyHowBlock[];
    defaultCtaLabel?: string;
    defaultCtaHref?: string;
    className?: string;
    pillRowClassName?: string;
    blocksWrapperClassName?: string;
};

/** Single column through tablet (`md`); 2–3 columns from `lg` only. */
function blockGridClass(cardCount: number) {
    if (cardCount >= 3) return 'lg:grid-cols-3';
    if (cardCount === 2) return 'lg:grid-cols-2';
    return 'lg:grid-cols-1';
}

function blockAnchorId(
    block: UseCasesWhyHowBlock,
    blockIndex: number,
): string {
    return block.anchorId ?? `usecases-whyhow-${blockIndex}`;
}

export function UseCasesWhyHow({
    sectionTitle,
    sectionTitleClassName,
    pillItems = [],
    pillNavMode = 'off',
    defaultActivePillId,
    activePillId: controlledActivePillId,
    onActivePillChange,
    blocks,
    defaultCtaLabel = 'Book a Demo',
    defaultCtaHref = '/demo',
    className,
    pillRowClassName,
    blocksWrapperClassName,
}: UseCasesWhyHowProps) {
    const resolvedPills = useMemo((): UseCasesWhyHowPill[] => {
        if (pillItems.length > 0) return pillItems;
        if (pillNavMode !== 'fromBlocks') return [];
        return blocks.map((block, i) => ({
            id: blockAnchorId(block, i),
            label: block.pillNavLabel ?? block.title,
        }));
    }, [pillItems, blocks, pillNavMode]);

    const preferredDefaultId =
        defaultActivePillId &&
        resolvedPills.some((p) => p.id === defaultActivePillId)
            ? defaultActivePillId
            : (resolvedPills[0]?.id ?? '');

    const [internalPillId, setInternalPillId] = useState(preferredDefaultId);

    const activePillId = controlledActivePillId ?? internalPillId;

    const setActive = useCallback(
        (id: string) => {
            if (onActivePillChange) onActivePillChange(id);
            if (controlledActivePillId == null) setInternalPillId(id);
        },
        [controlledActivePillId, onActivePillChange],
    );

    const scrollToPillTarget = useCallback(
        (pillId: string) => {
            const pillIndex = resolvedPills.findIndex((p) => p.id === pillId);
            const byAnchor = blocks.findIndex(
                (b, i) => blockAnchorId(b, i) === pillId,
            );
            const blockIndex =
                pillIndex >= 0 && pillIndex < blocks.length
                    ? pillIndex
                    : byAnchor >= 0
                      ? byAnchor
                      : -1;
            if (blockIndex < 0) return;
            const targetId = blockAnchorId(blocks[blockIndex], blockIndex);
            const el =
                typeof document !== 'undefined'
                    ? document.getElementById(targetId)
                    : null;
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        [blocks, resolvedPills],
    );

    const onPillClick = useCallback(
        (pillId: string) => {
            setActive(pillId);
            scrollToPillTarget(pillId);
        },
        [setActive, scrollToPillTarget],
    );

    return (
        <section
            className={clsx(
                'overflow-hidden bg-brand-50 px-5 py-16 md:px-10 lg:px-[79px]',
                className,
            )}
        >
            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-12">
                <Elements.Title
                    title={sectionTitle}
                    type="h2"
                    className={clsx(
                        'text-center text-3xl font-semibold text-gray-900 md:text-4xl',
                        sectionTitleClassName,
                    )}
                />

                {resolvedPills.length > 0 ? (
                    <div
                        className={clsx(
                            'flex flex-wrap items-center justify-center gap-2 sm:gap-3',
                            pillRowClassName,
                        )}
                        role="tablist"
                        aria-label={sectionTitle}
                    >
                        {resolvedPills.map((pill) => {
                            const isActive = pill.id === activePillId;
                            return (
                                <button
                                    key={pill.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => onPillClick(pill.id)}
                                    className={clsx(
                                        'max-w-[min(100%,20rem)] rounded-full text-balance text-center text-sm leading-snug transition-colors sm:text-base md:text-base',
                                        'px-4 py-2.5 sm:px-5 sm:py-2 cursor-pointer',
                                        isActive
                                            ? 'bg-brand-700 font-normal text-gray-50'
                                            : ' font-normal text-brand-500 hover:text-gray-50 hover:bg-brand-700 ',
                                    )}
                                >
                                    {pill.label}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div
                    className={clsx(
                        'flex flex-col gap-16 md:gap-20',
                        blocksWrapperClassName,
                    )}
                >
                    {blocks.map((block, blockIndex) => {
                        const ctaLabel = block.ctaLabel ?? defaultCtaLabel;
                        const ctaHref = block.ctaHref ?? defaultCtaHref;
                        const grid = blockGridClass(block.cards.length);

                        return (
                            <article
                                key={`${block.title}-${blockIndex}`}
                                id={blockAnchorId(block, blockIndex)}
                                className="scroll-mt-32"
                            >
                                <div
                                    className={clsx(
                                        'flex w-full flex-col gap-8 rounded-2xl bg-gray-50 p-6 shadow-[0px_0px_5px_0px_#00000017,2px_2px_7px_0px_#0000000D] md:p-10',
                                        block.containerClassName,
                                    )}
                                >
                                    <div className=" flex w-full flex-col gap-4 text-start">
                                        <Elements.Title
                                            title={block.title}
                                            type="h3"
                                            className="text-2xl font-semibold text-gray-900 md:text-5xl!"
                                        />
                                        <Elements.Description
                                            description={block.description}
                                            type="medium"
                                            className="text-balance text-gray-700"
                                        />
                                    </div>

                                    <div
                                        className={clsx(
                                            'grid w-full grid-cols-1 gap-4 lg:gap-6',
                                            grid,
                                        )}
                                    >
                                        {block.cards.map((card, i) => (
                                            <div
                                                key={`${card.title}-${i}`}
                                                className="flex h-full flex-col rounded-2xl border border-gray-200/60 bg-gray-50/80 p-6"
                                            >
                                                <div className="mb-4 bg-brand-100 h-12 w-12 rounded-lg flex justify-center items-center">
                                                    <Image
                                                        src={card.iconSrc}
                                                        alt={card.iconAlt ?? ''}
                                                        width={40}
                                                        height={40}
                                                        className="h-8 w-8 object-contain"
                                                    />
                                                </div>
                                                <Elements.Title
                                                    title={card.title}
                                                    type="h5"
                                                    className="mb-2 text-left text-lg! font-medium text-gray-900 md:text-xl!"
                                                />
                                                <Elements.Description
                                                    description={card.description}
                                                    type="small"
                                                    className="text-left text-gray-500"
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-center pt-2">
                                        <Link to={ctaHref}>
                                            <Elements.Button
                                                variant="primary"
                                                size="lg"
                                            >
                                                {ctaLabel}
                                            </Elements.Button>
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
